import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, ChevronDown, UserPlus, X, Search, Users, Loader2 } from 'lucide-react';
import { portalGroupAPI, staffAPI } from '../../services/api';
import toast from '../../utils/toast';

const GROUP_DESCRIPTIONS = {
  HR: 'Used by selected HR workflows and notifications. It does not grant Admin Portal access.',
  Manager: 'Manager-level claim submissions and approval capabilities',
  CourseNominator: 'Can nominate workers for course / training requests (company-wide)',
};

const GROUP_COLORS = {
  HR: { badge: 'bg-red-100 text-red-800', dot: 'bg-red-500' },
  Manager: { badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  CourseNominator: { badge: 'bg-teal-100 text-teal-800', dot: 'bg-teal-500' },
};

const PortalGroupsCard = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await portalGroupAPI.getGroups();
      setGroups(data);
    } catch {
      toast.error('Failed to load portal groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  useEffect(() => {
    if (addingTo && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [addingTo]);

  const searchStaff = useCallback((term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term || term.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await staffAPI.getStaffList({ search: term, page_size: 10 });
        const results = data.results || data;
        setSearchResults(Array.isArray(results) ? results : []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleAddMember = async (groupName, staffId) => {
    setActionLoading(`add-${groupName}-${staffId}`);
    try {
      await portalGroupAPI.addMember(groupName, staffId);
      toast.success('Member added');
      setSearchTerm('');
      setSearchResults([]);
      setAddingTo(null);
      await fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add member';
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async (groupName, staffId, name) => {
    if (!window.confirm(`Remove ${name} from ${groupName} group?`)) return;
    setActionLoading(`remove-${groupName}-${staffId}`);
    try {
      await portalGroupAPI.removeMember(groupName, staffId);
      toast.success('Member removed');
      await fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to remove member';
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleExpand = (groupName) => {
    if (expandedGroup === groupName) {
      setExpandedGroup(null);
      setAddingTo(null);
    } else {
      setExpandedGroup(groupName);
      setAddingTo(null);
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  const openAddMember = (groupName) => {
    setAddingTo(groupName);
    setSearchTerm('');
    setSearchResults([]);
  };

  const closeAddMember = () => {
    setAddingTo(null);
    setSearchTerm('');
    setSearchResults([]);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 col-span-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-gray-200 rounded animate-pulse" />
            <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
        <div className="px-6 py-8">
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading portal groups...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow col-span-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900">Portal Groups</h3>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 flex-shrink-0">
            {groups.length} {groups.length === 1 ? 'group' : 'groups'}
          </span>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          These groups are separate from Admin Portal access. Accounts with Admin Access or Superuser status are listed in Admin Users above.
        </p>
      </div>

      {/* Groups list */}
      <div className="px-6 py-4 space-y-3">
        {groups.map((group) => {
          const colors = GROUP_COLORS[group.name] || { badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-500' };
          const isExpanded = expandedGroup === group.name;
          const isAdding = addingTo === group.name;
          const currentMemberIds = group.members.map((m) => m.staff_id);

          return (
            <div
              key={group.id}
              className="border border-gray-200 rounded-xl overflow-hidden transition-all"
            >
              {/* Group header row */}
              <button
                onClick={() => toggleExpand(group.name)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                  <span className="font-medium text-gray-900">{group.name}</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                    {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="border-t border-gray-200 px-4 py-3 bg-gray-50/50">
                  {/* Description */}
                  {GROUP_DESCRIPTIONS[group.name] && (
                    <p className="text-sm text-gray-500 mb-3">
                      {GROUP_DESCRIPTIONS[group.name]}
                    </p>
                  )}

                  {/* Add member button / search */}
                  {!isAdding ? (
                    <button
                      onClick={() => openAddMember(group.name)}
                      className="btn btn-admin-primary btn-sm mb-3"
                    >
                      <UserPlus className="w-4 h-4" />
                      Add Member
                    </button>
                  ) : (
                    <div className="mb-3 space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          className="input-admin pl-10 pr-10"
                          placeholder="Search by name or staff ID..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            searchStaff(e.target.value);
                          }}
                        />
                        <button
                          onClick={closeAddMember}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                          aria-label="Close search"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>

                      {/* Search results dropdown */}
                      {(searchTerm.length >= 2) && (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm max-h-48 overflow-y-auto">
                          {searching ? (
                            <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Searching...
                            </div>
                          ) : searchResults.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-400">
                              No employees found
                            </div>
                          ) : (
                            searchResults.map((staff) => {
                              const alreadyMember = currentMemberIds.includes(staff.staff_id);
                              const isLoadingThis = actionLoading === `add-${group.name}-${staff.staff_id}`;
                              return (
                                <button
                                  key={staff.staff_id}
                                  disabled={alreadyMember || isLoadingThis}
                                  onClick={() => handleAddMember(group.name, staff.staff_id)}
                                  className={`w-full text-left px-4 py-2.5 flex items-center justify-between text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-inset ${
                                    alreadyMember
                                      ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                      : 'hover:bg-red-50 text-gray-700'
                                  }`}
                                >
                                  <div className="min-w-0">
                                    <span className="font-medium">
                                      {staff.first_name} {staff.last_name}
                                    </span>
                                    <span className="text-gray-400 ml-2">{staff.staff_id}</span>
                                    {staff.designation && (
                                      <span className="text-gray-400 ml-2">· {staff.designation}</span>
                                    )}
                                  </div>
                                  {alreadyMember ? (
                                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">Already member</span>
                                  ) : isLoadingThis ? (
                                    <Loader2 className="w-4 h-4 animate-spin text-red-500 flex-shrink-0 ml-2" />
                                  ) : (
                                    <UserPlus className="w-4 h-4 text-red-500 flex-shrink-0 ml-2" />
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Members list */}
                  {group.members.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                      <Users className="w-4 h-4" />
                      No members in this group
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {group.members.map((member) => {
                        const isRemoving = actionLoading === `remove-${group.name}-${member.staff_id}`;
                        return (
                          <div
                            key={member.staff_id}
                            className="group flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                                {(member.first_name?.[0] || '').toUpperCase()}
                                {(member.last_name?.[0] || '').toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900">
                                  {member.first_name} {member.last_name}
                                </span>
                                <span className="text-xs text-gray-400 ml-2">{member.staff_id}</span>
                                {member.designation && (
                                  <p className="text-xs text-gray-500 truncate">{member.designation}</p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() =>
                                handleRemoveMember(
                                  group.name,
                                  member.staff_id,
                                  `${member.first_name} ${member.last_name}`
                                )
                              }
                              disabled={isRemoving}
                              className="icon-button-danger opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              title="Remove from group"
                              aria-label={`Remove ${member.first_name} ${member.last_name} from ${group.name}`}
                            >
                              {isRemoving ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {groups.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            No portal groups configured. Run migrations to create default groups.
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalGroupsCard;
